<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
      @click.self="emit('close')"
    >
      <div
        class="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-2xl border border-white/20 dark:border-slate-700/50 overflow-hidden"
        style="max-height: 90vh"
        @click.stop
      >
        <!-- Header -->
        <div class="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-700 dark:to-slate-800 px-5 py-4 flex items-center justify-between border-b border-slate-200/50 dark:border-slate-600/50 rounded-t-3xl flex-shrink-0">
          <div class="flex items-center gap-2">
            <CreditCard class="w-5 h-5 text-slate-600 dark:text-slate-300" />
            <h2 class="font-semibold text-slate-800 dark:text-white">Meine Visitenkarte</h2>
          </div>
          <button
            @click="emit('close')"
            class="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-600/50 rounded-xl transition-colors"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="flex flex-col items-center gap-3">
            <Loader2 class="w-8 h-8 animate-spin text-blue-500" />
            <p class="text-sm text-slate-500 dark:text-slate-400">Lade Kontaktdaten...</p>
          </div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="flex items-center justify-center py-20 px-5">
          <div class="text-center">
            <p class="text-sm text-red-500 mb-3">{{ error }}</p>
            <button
              @click="loadCard"
              class="text-xs px-4 py-2 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors"
            >
              Erneut versuchen
            </button>
          </div>
        </div>

        <!-- Content -->
        <div v-else-if="contact" class="overflow-y-auto" style="max-height: calc(90vh - 64px)">
          <div class="p-5 sm:p-6 flex flex-col items-center gap-5">
            <BrandedBusinessCard :contact="contact" :photo-url="photoUrl">
              <template #qr><canvas ref="qrCanvas" /></template>
            </BrandedBusinessCard>

            <p class="text-xs text-slate-500 dark:text-slate-400 text-center">
              Kamera draufhalten → Kontakt direkt speichern
            </p>

            <div class="w-full max-w-sm flex flex-col items-center gap-3">
              <div class="flex gap-2 w-full">
                <button
                  @click="downloadQrCode"
                  class="flex-1 py-2.5 text-sm font-medium rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Download class="w-4 h-4" />
                  QR-Bild
                </button>
                <button
                  @click="downloadVcf"
                  class="flex-1 py-2.5 text-sm font-medium rounded-xl bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60 text-blue-600 dark:text-blue-400 flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Download class="w-4 h-4" />
                  .vcf Kontakt
                </button>
              </div>

              <div
                v-if="walletBrandOptions.length > 0"
                class="w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/60 p-2"
              >
                <p class="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
                  Apple-Wallet-Design testen
                </p>
                <div class="grid grid-cols-3 gap-1" role="group" aria-label="Apple-Wallet-Design">
                  <button
                    v-for="option in walletBrandOptions"
                    :key="option.key"
                    type="button"
                    @click="selectedWalletBrand = option.key"
                    class="min-h-10 rounded-lg px-2 py-1.5 text-xs font-medium leading-tight transition-colors"
                    :class="selectedWalletBrand === option.key
                      ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm ring-1 ring-blue-200 dark:ring-blue-800'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-white/70 dark:hover:bg-slate-800/70'"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <button
                v-if="walletAvailable"
                @click="downloadWalletPass"
                :disabled="walletLoading"
                class="w-full py-2.5 text-sm font-medium rounded-xl bg-black hover:bg-gray-800 text-white flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                <Loader2 v-if="walletLoading" class="w-4 h-4 animate-spin" />
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                Zur Apple Wallet hinzufügen
              </button>

              <p class="text-xs text-slate-400 dark:text-slate-500 text-center leading-relaxed">
                .vcf enthält alle Daten inkl. Foto — auf iOS/Android direkt in Kontakte importierbar
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { X, CreditCard, Download, Loader2 } from 'lucide-vue-next';
import { authenticatedFetch, getUserInfo, getIsInTeams } from '../oauth2-config';
import * as microsoftTeams from '@microsoft/teams-js';
import QRCode from 'qrcode';
import BrandedBusinessCard from './BrandedBusinessCard.vue';
import { buildVCard } from '../utils/vcard.mjs';

const emit = defineEmits(['close']);

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

const contact = ref(null);
const photoUrl = ref(null);
const photoBase64 = ref(null);
const loading = ref(true);
const error = ref(null);
const qrCanvas = ref(null);
const walletAvailable = ref(false);
const walletLoading = ref(false);
const walletBrandOptions = ref([]);
const selectedWalletBrand = ref('');

const syncWalletBrandSelection = () => {
  if (walletBrandOptions.value.length === 0 || selectedWalletBrand.value) return;
  const currentBrand = contact.value?.branding?.key;
  selectedWalletBrand.value = walletBrandOptions.value.some(option => option.key === currentBrand)
    ? currentBrand
    : walletBrandOptions.value[0].key;
};

const loadCard = async () => {
  loading.value = true;
  error.value = null;

  const userInfo = getUserInfo();
  if (!userInfo?.email) {
    error.value = 'Benutzerinformationen nicht verfügbar.';
    loading.value = false;
    return;
  }

  // Fetch own contact record
  try {
    const res = await authenticatedFetch(`${API_URL}/api/contacts/${encodeURIComponent(userInfo.email)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    contact.value = await res.json();
    syncWalletBrandSelection();
  } catch {
    error.value = 'Kontaktdaten konnten nicht geladen werden.';
    loading.value = false;
    return;
  }

  // Fetch photo (optional — continue without it)
  try {
    const photoRes = await authenticatedFetch(`${API_URL}/api/contacts/${encodeURIComponent(userInfo.email)}/photo`);
    if (photoRes.ok) {
      const blob = await photoRes.blob();
      // Revoke any previously created blob URL
      if (photoUrl.value) URL.revokeObjectURL(photoUrl.value);
      photoUrl.value = URL.createObjectURL(blob);
      // Convert to base64 for vCard photo embedding
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          photoBase64.value = reader.result.split(',')[1];
          resolve();
        };
        reader.onerror = resolve;
        reader.readAsDataURL(blob);
      });
    }
  } catch {
    // Photo is optional — silently continue
  }

  loading.value = false;
  await nextTick();
  renderQrCode();
};

const renderQrCode = async () => {
  await nextTick();
  if (!qrCanvas.value || !contact.value) return;
  const vcard = buildVCard(contact.value); // no photo in QR — keeps it scannable
  try {
    await QRCode.toCanvas(qrCanvas.value, vcard, {
      width: 240,
      margin: 1,
      errorCorrectionLevel: 'H',
      color: { dark: '#1e293b', light: '#ffffff' }
    });

    // Overlay profile photo in the center of the QR code
    if (photoUrl.value) {
      const canvas = qrCanvas.value;
      const ctx = canvas.getContext('2d');
      const size = canvas.width;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
        img.src = photoUrl.value;
      });
      if (img.complete && img.naturalWidth > 0) {
        const photoSize = Math.round(size * 0.22);
        const x = (size - photoSize) / 2;
        const y = (size - photoSize) / 2;
        const border = Math.round(size * 0.018);
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, photoSize / 2 + border, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.save();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, photoSize / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, x, y, photoSize, photoSize);
        ctx.restore();
      }
    }
  } catch (e) {
    console.error('QR generation failed:', e);
  }
};

const downloadQrCode = () => {
  if (!qrCanvas.value || !contact.value) return;
  const link = document.createElement('a');
  link.download = `${(contact.value.display_name || 'visitenkarte').replace(/\s+/g, '_')}_QR.png`;
  link.href = qrCanvas.value.toDataURL('image/png');
  link.click();
};

const downloadVcf = () => {
  if (!contact.value) return;
  const vcard = buildVCard(contact.value, { includePhoto: true, photoBase64: photoBase64.value });
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(contact.value.display_name || 'visitenkarte').replace(/\s+/g, '_')}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const checkWalletStatus = async () => {
  try {
    const res = await authenticatedFetch(`${API_URL}/api/me/wallet/apple/status`);
    if (res.ok) {
      const data = await res.json();
      walletAvailable.value = data.available;
      walletBrandOptions.value = Array.isArray(data.brandOptions) ? data.brandOptions : [];
      syncWalletBrandSelection();
    }
  } catch {
    // Wallet check is optional
  }
};

const downloadWalletPass = async () => {
  if (walletLoading.value) return;
  walletLoading.value = true;
  try {
    // Get one-time download token
    const tokenRes = await authenticatedFetch(`${API_URL}/api/me/wallet/apple/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ brand: selectedWalletBrand.value || undefined }),
    });
    if (!tokenRes.ok) throw new Error(`Token HTTP ${tokenRes.status}`);
    const { url } = await tokenRes.json();
    const fullUrl = `${API_URL}${url}`;

    if (getIsInTeams()) {
      // In Teams: open in system browser (Safari on iOS) so .pkpass triggers Apple Wallet
      await microsoftTeams.app.openLink(fullUrl);
    } else {
      // Normal browser: navigate directly
      window.location.href = fullUrl;
    }
  } catch (e) {
    console.error('Wallet download failed:', e);
  } finally {
    walletLoading.value = false;
  }
};

const onKeyDown = (e) => {
  if (e.key === 'Escape') emit('close');
};

onMounted(() => {
  document.addEventListener('keydown', onKeyDown);
  loadCard();
  checkWalletStatus();
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeyDown);
  if (photoUrl.value) URL.revokeObjectURL(photoUrl.value);
});
</script>
